import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import type { EvidenceAnalysis } from '@fixtyer/shared';
import { trackPackValuePreviewViewed } from '@/lib/analytics';

interface EvidenceScoreProps {
  score: number;
  analysis?: EvidenceAnalysis | null;
  dedupeScope?: string;
}

export function EvidenceScore({ score, analysis, dedupeScope }: EvidenceScoreProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  useEffect(() => {
    trackPackValuePreviewViewed(score, dedupeScope);
  }, [score, dedupeScope]);
  
  const getScoreColor = (s: number) => {
    if (s >= 70) return 'text-success-500';
    if (s >= 40) return 'text-warning-500';
    return 'text-error-500';
  };

  const getScoreLabel = (s: number) => {
    if (s >= 70) return 'Strong';
    if (s >= 40) return 'Building';
    return 'Needs Evidence';
  };

  const aiReasoning = analysis?.aiReasoning as any;
  const evidenceCategories = aiReasoning?.evidenceCategories;
  const priorityActions = aiReasoning?.priorityActions;
  const photoGuidance = aiReasoning?.photoGuidance;
  // Support both new (macSummary) and old (plainEnglishSummary) formats
  const macSummary = aiReasoning?.macSummary || aiReasoning?.plainEnglishSummary;
  const overallCompleteness = aiReasoning?.overallCompleteness;

  // Get headline based on completeness status (new format) or use provided headline
  const getCompletenessHeadline = () => {
    if (macSummary?.headline) return macSummary.headline;
    switch (overallCompleteness) {
      case 'well-documented': return "I've been through everything. You're well prepared.";
      case 'mostly-complete': return "Most of what I need is here. A few things to tidy up.";
      case 'needs-work': return "You've made a start, but there are gaps to fill.";
      case 'early-stage': return "Early days. Let's get the basics in place first.";
      default: return "Here's where you're at.";
    }
  };

  // Get items - support both new (whatsHere) and old (whatsGood) field names
  const whatsHere = macSummary?.whatsHere || macSummary?.whatsGood || [];
  const whatsMissing = macSummary?.whatsMissing || [];

  return (
    <div className="space-y-4">
      {/* Mac's Evidence Review - Most prominent */}
      {macSummary && (
        <div className="bg-gradient-to-br from-brand-50 via-white to-accent-50 rounded-2xl border-2 border-brand-200 p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <img 
              src="/mac-avatar.png" 
              alt="Mac" 
              className="w-14 h-14 rounded-full flex-shrink-0 object-cover"
            />
            <div className="flex-1">
              <p className="text-sm text-surface-500 mb-1">Mac's checked your evidence</p>
              <h2 className="font-display text-xl font-bold text-brand-950 mb-3">
                {getCompletenessHeadline()}
              </h2>
              
              {/* What you've got */}
              {whatsHere?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-success-700 mb-1.5 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-success-100 flex items-center justify-center text-xs">✓</span>
                    What you've got
                  </h3>
                  <ul className="space-y-1">
                    {whatsHere.map((item: string, i: number) => (
                      <li key={i} className="text-sm text-surface-700 pl-6">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* What's missing */}
              {whatsMissing?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-amber-700 mb-1.5 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-xs">⚠</span>
                    Worth adding
                  </h3>
                  <ul className="space-y-1">
                    {whatsMissing.map((item: string, i: number) => (
                      <li key={i} className="text-sm text-surface-700 pl-6">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Next Step - Call to Action */}
              {macSummary.nextStep && (
                <div className="mt-4 p-4 bg-brand-600 rounded-xl text-white">
                  <p className="text-sm font-medium mb-1">→ Next thing to do:</p>
                  <p className="text-sm opacity-95">{macSummary.nextStep}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Score Card */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-brand-950">
            Evidence Strength
          </h2>
        </div>

        <div className="flex items-start gap-6 mb-6">
          {/* Circular progress */}
          <div className="relative w-28 h-28 flex-shrink-0">
            <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-surface-100"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeDasharray={`${(score / 100) * 283} 283`}
                strokeLinecap="round"
                className={getScoreColor(score)}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-3xl font-bold text-brand-950">
                {score}
              </span>
              <span className="text-xs text-surface-600 font-medium">/ 100</span>
            </div>
          </div>

          <div className="flex-1">
            <p className={clsx('font-semibold text-xl mb-1', getScoreColor(score))}>
              {getScoreLabel(score)}
            </p>
            {aiReasoning?.overallAssessment ? (
              <p className="text-sm text-surface-700 leading-relaxed">
                {aiReasoning.overallAssessment}
              </p>
            ) : (
              <p className="text-sm text-surface-700">
                {score >= 70
                  ? 'Your evidence pack is ready for submission!'
                  : score >= 40
                  ? 'You\'re making progress. Follow the recommendations below to strengthen your claim.'
                  : 'Your claim needs more evidence. Focus on the priority actions below.'}
              </p>
            )}
          </div>
        </div>

        {/* Category Breakdown */}
        {evidenceCategories && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-brand-950">Evidence Breakdown</h3>
            <div className="grid gap-2">
              <CategoryBar 
                label="Pothole Evidence" 
                score={evidenceCategories.defectEvidence?.score || 0} 
                maxScore={35}
                status={evidenceCategories.defectEvidence?.status}
                icon="🕳️"
              />
              <CategoryBar 
                label="Prior Reports" 
                score={evidenceCategories.priorKnowledge?.score || 0} 
                maxScore={25}
                status={evidenceCategories.priorKnowledge?.status}
                icon="📋"
                highlight={evidenceCategories.priorKnowledge?.status === 'missing'}
              />
              <CategoryBar 
                label="Damage Evidence" 
                score={evidenceCategories.damageEvidence?.score || 0} 
                maxScore={20}
                status={evidenceCategories.damageEvidence?.status}
                icon="🚗"
              />
              <CategoryBar 
                label="Causation Chain" 
                score={evidenceCategories.causationChain?.score || 0} 
                maxScore={15}
                status={evidenceCategories.causationChain?.status}
                icon="🔗"
              />
              <CategoryBar 
                label="Financial Docs" 
                score={evidenceCategories.financialDocs?.score || 0} 
                maxScore={5}
                status={evidenceCategories.financialDocs?.status}
                icon="🧾"
              />
            </div>
          </div>
        )}

        {/* What would make this 90+ */}
        {aiReasoning?.whatWouldMakeThis90Plus && score < 90 && (
          <div className="mt-4 p-4 bg-gradient-to-r from-brand-50 to-accent-50 rounded-xl border border-brand-200">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💡</span>
              <div>
                <h4 className="text-sm font-semibold text-brand-900 mb-1">
                  To reach 90+ score:
                </h4>
                <p className="text-sm text-brand-700">
                  {aiReasoning.whatWouldMakeThis90Plus}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Priority Actions Card */}
      {priorityActions && priorityActions.length > 0 && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
            🎯 Priority Actions
          </h3>
          <div className="space-y-3">
            {priorityActions.slice(0, 5).map((action: any, index: number) => (
              <div 
                key={index}
                className={clsx(
                  'p-4 rounded-xl border transition-all cursor-pointer',
                  action.impact === 'high' 
                    ? 'bg-error-50 border-error-200 hover:border-error-300' 
                    : action.impact === 'medium'
                    ? 'bg-warning-50 border-warning-200 hover:border-warning-300'
                    : 'bg-surface-50 border-surface-200 hover:border-surface-300',
                  expandedSection === `action-${index}` && 'ring-2 ring-brand-200'
                )}
                onClick={() => setExpandedSection(
                  expandedSection === `action-${index}` ? null : `action-${index}`
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={clsx(
                        'text-xs font-bold px-2 py-0.5 rounded',
                        action.impact === 'high' 
                          ? 'bg-error-200 text-error-800' 
                          : action.impact === 'medium'
                          ? 'bg-warning-200 text-warning-800'
                          : 'bg-surface-200 text-surface-700'
                      )}>
                        {action.impact?.toUpperCase()} IMPACT
                      </span>
                    </div>
                    <p className="font-medium text-brand-950">{action.action}</p>
                    {action.why && (
                      <p className="text-sm text-surface-600 mt-1">{action.why}</p>
                    )}
                  </div>
                  <svg 
                    className={clsx(
                      'w-5 h-5 text-surface-400 transition-transform',
                      expandedSection === `action-${index}` && 'rotate-180'
                    )} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {expandedSection === `action-${index}` && action.howTo && (
                  <div className="mt-3 pt-3 border-t border-surface-200">
                    <p className="text-sm font-medium text-brand-900 mb-1">How to do this:</p>
                    <p className="text-sm text-surface-700">{action.howTo}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo Guidance Card */}
      {photoGuidance && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
            📸 Photo Guidance
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Pothole Photos */}
            {photoGuidance.potholePhotos && (
              <div className="p-4 bg-surface-50 rounded-xl">
                <h4 className="font-medium text-brand-950 mb-2 flex items-center gap-2">
                  🕳️ Pothole Photos
                </h4>
                {photoGuidance.potholePhotos.current && (
                  <p className="text-sm text-surface-600 mb-2">
                    <span className="font-medium">Current:</span> {photoGuidance.potholePhotos.current}
                  </p>
                )}
                {photoGuidance.potholePhotos.needed?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-sm font-medium text-brand-900 mb-1">Needed:</p>
                    <ul className="space-y-1">
                      {photoGuidance.potholePhotos.needed.map((item: string, i: number) => (
                        <li key={i} className="text-sm text-surface-700 flex items-start gap-2">
                          <span className="text-brand-500 mt-0.5">→</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {photoGuidance.potholePhotos.tips?.length > 0 && (
                  <div className="mt-2 p-2 bg-brand-50 rounded-lg">
                    <p className="text-xs font-medium text-brand-800 flex items-center gap-1">
                      <span>💡</span> Mac's tips:
                    </p>
                    {photoGuidance.potholePhotos.tips.map((tip: string, i: number) => (
                      <p key={i} className="text-xs text-brand-700 ml-5">{tip}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Damage Photos */}
            {photoGuidance.damagePhotos && (
              <div className="p-4 bg-surface-50 rounded-xl">
                <h4 className="font-medium text-brand-950 mb-2 flex items-center gap-2">
                  🚗 Damage Photos
                </h4>
                {photoGuidance.damagePhotos.current && (
                  <p className="text-sm text-surface-600 mb-2">
                    <span className="font-medium">Current:</span> {photoGuidance.damagePhotos.current}
                  </p>
                )}
                {photoGuidance.damagePhotos.needed?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-sm font-medium text-brand-900 mb-1">Needed:</p>
                    <ul className="space-y-1">
                      {photoGuidance.damagePhotos.needed.map((item: string, i: number) => (
                        <li key={i} className="text-sm text-surface-700 flex items-start gap-2">
                          <span className="text-brand-500 mt-0.5">→</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {photoGuidance.damagePhotos.tips?.length > 0 && (
                  <div className="mt-2 p-2 bg-brand-50 rounded-lg">
                    <p className="text-xs font-medium text-brand-800 flex items-center gap-1">
                      <span>💡</span> Mac's tips:
                    </p>
                    {photoGuidance.damagePhotos.tips.map((tip: string, i: number) => (
                      <p key={i} className="text-xs text-brand-700 ml-5">{tip}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Safety Guidance Card - Always visible */}
      <div className="bg-pink-50 rounded-2xl border-2 border-red-300 p-6">
        <h3 className="font-display text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
          ⚠️ Important Safety Guidance
        </h3>
        <div className="space-y-3 text-sm text-red-900">
          <p>
            <strong>Only attempt to photograph a pothole where it is safe to do so.</strong> Your safety is more important than your claim.
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">•</span>
              <span>Take another person with you where possible — they can act as a lookout and assist if needed.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">•</span>
              <span>Wear high-visibility clothing, particularly on rural roads or in low-light conditions.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">•</span>
              <span><strong>Never stop or exit your vehicle on a motorway</strong> — it is unlawful and extremely dangerous. If the pothole is on a motorway, try to capture it safely from an overhead bridge or footpath if one is nearby.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">•</span>
              <span>Avoid standing in active traffic lanes. If the pothole is in a dangerous position, consider whether it's possible to photograph it during quieter periods or from the roadside verge.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Evidence Checklist - Always visible with clear status */}
      {analysis && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-brand-950">
              Evidence Checklist
            </h3>
            <span className="text-sm text-surface-500">
              {[
                analysis.potholePhotoPresent,
                analysis.damagePhotoPresent,
                analysis.invoicePresent,
                analysis.locationComplete || analysis.locationVerified,
                analysis.measurementVisible,
                analysis.priorReportsConfirmed > 0
              ].filter(Boolean).length}/6 items
            </span>
          </div>
          <div className="grid gap-2">
            <ChecklistItem
              label="Pothole photos"
              checked={analysis.potholePhotoPresent}
              detail={analysis.potholePhotoCount ? `${analysis.potholePhotoCount} uploaded` : undefined}
            />
            <ChecklistItem
              label="Damage photos"
              checked={analysis.damagePhotoPresent}
              detail={analysis.damagePhotoCount ? `${analysis.damagePhotoCount} uploaded` : undefined}
            />
            <ChecklistItem
              label="Repair invoice/quote"
              checked={analysis.invoicePresent}
              detail={analysis.invoiceExtractedAmount ? `£${Number(analysis.invoiceExtractedAmount).toFixed(2)} found` : undefined}
            />
            <ChecklistItem
              label="Location pinned on map"
              checked={analysis.locationComplete || analysis.locationVerified || false}
              detail={analysis.locationVerified ? 'GPS verified' : undefined}
            />
            <ChecklistItem
              label="Depth measurement shown"
              checked={analysis.measurementVisible || false}
              important
            />
            <ChecklistItem
              label="Prior council reports"
              checked={analysis.priorReportsConfirmed > 0}
              detail={analysis.priorReportsConfirmed > 0 ? `${analysis.priorReportsConfirmed} matched` : undefined}
              important
            />
          </div>
          
        </div>
      )}

      {/* Fallback Recommendations */}
      {analysis?.recommendations && analysis.recommendations.length > 0 && !priorityActions && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
            Recommendations
          </h3>
          <ul className="space-y-2">
            {analysis.recommendations.map((rec, index) => (
              <li key={index} className="text-sm text-surface-700 flex items-start gap-3 p-3 bg-surface-50 rounded-lg">
                <span className="flex-shrink-0">{rec.startsWith('📸') || rec.startsWith('📏') || rec.startsWith('🚗') || rec.startsWith('🧾') || rec.startsWith('📍') || rec.startsWith('⭐') ? '' : '•'}</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Legal Context */}
      {aiReasoning?.legalNotes && (
        <div className="bg-surface-50 rounded-2xl border border-surface-200 p-6">
          <h3 className="text-sm font-semibold text-surface-600 mb-2 flex items-center gap-2">
            ⚖️ Legal Context
          </h3>
          <p className="text-sm text-surface-600">{aiReasoning.legalNotes}</p>
        </div>
      )}
    </div>
  );
}

interface CategoryBarProps {
  label: string;
  score: number;
  maxScore: number;
  status?: string;
  icon: string;
  highlight?: boolean;
}

function CategoryBar({ label, score, maxScore, status, icon, highlight }: CategoryBarProps) {
  const percentage = Math.round((score / maxScore) * 100);
  
  const getStatusColor = (s?: string) => {
    switch (s) {
      case 'strong': return 'bg-success-500';
      case 'adequate': return 'bg-warning-500';
      case 'weak': return 'bg-orange-500';
      case 'missing': return 'bg-error-500';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className={clsx(
      'p-3 rounded-lg transition-colors',
      highlight ? 'bg-amber-50 border border-amber-200' : 'bg-surface-50'
    )}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-brand-950 flex items-center gap-2">
          <span>{icon}</span>
          {label}
          {highlight && <span className="text-xs text-amber-600">(Key evidence)</span>}
        </span>
        <span className="text-xs font-medium text-surface-600">{score}/{maxScore}</span>
      </div>
      <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
        <div 
          className={clsx('h-full rounded-full transition-all', getStatusColor(status))}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

interface ChecklistItemProps {
  label: string;
  checked: boolean;
  detail?: string;
  important?: boolean;
}

function ChecklistItem({ label, checked, detail, important }: ChecklistItemProps) {
  return (
    <div className={clsx(
      'flex items-center justify-between py-2.5 px-3 rounded-lg',
      important && !checked ? 'bg-amber-50 border border-amber-200' : 'bg-surface-50'
    )}>
      <div className="flex items-center gap-3">
        <div
          className={clsx(
            'w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold',
            checked
              ? 'bg-success-500 text-white'
              : important
              ? 'bg-amber-400 text-white'
              : 'bg-surface-300 text-surface-500'
          )}
        >
          {checked ? (
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          ) : important ? (
            <span>!</span>
          ) : (
            <span className="text-xs">—</span>
          )}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-brand-950">{label}</span>
          {important && !checked && (
            <span className="text-xs text-amber-600">(high impact evidence)</span>
          )}
        </div>
      </div>
      {detail && (
        <span className="text-sm text-success-600 font-medium">{detail}</span>
      )}
      {!checked && !detail && (
        <span className="text-xs text-surface-500">Missing</span>
      )}
    </div>
  );
}
